import { ITableSort, localStorageEnum } from 'types';
import moment, { Moment } from 'moment';
import {
  ITaskFilters,
  taskFilterEnum,
  TaskStatusType,
} from 'app/containers/Task/slice/types';
import {
  ISimpleUser,
  IUserListResponseItem,
} from 'app/containers/User/slice/types';
import { IUser } from 'app/containers/Authorization/slice/types';
import i18next from 'i18next';

export function authHeader() {
  // return authorization header with basic auth credentials
  let token = localStorage.getItem(localStorageEnum.token);

  if (token) {
    return { Authorization: token };
  } else {
    return undefined;
  }
}

export const capitalize = (s: string) => {
  if (typeof s !== 'string') return '';
  return s.charAt(0).toUpperCase() + s.slice(1);
};

export const getFormattedDateFilterValue = (
  date: Moment | undefined,
  filterType: 'start' | 'end',
) => {
  if (!date) {
    return;
  }
  let momentDate = moment(date).startOf('date');
  if (filterType === 'end') {
    momentDate = moment(date).endOf('date');
  }

  return momentDate.format('YYYY-MM-DDTHH:mm:ss');
};

export const toFixedIfFloat = (value: number) => {
  const isFloat = Boolean(value % 1);
  if (!isNaN(value) && isFloat) {
    const fixed = Number(value.toFixed(2));
    const isFixedFloat = Boolean(fixed % 1);

    if (isFixedFloat) {
      return fixed;
    } else {
      return Number(fixed.toFixed(0));
    }
  }
  return value;
};

export const getAreaWithUnits = (area: number) => {
  return `${toFixedIfFloat(area / 10000)} ha`;
};

export function groupBy(list: any, keyGetter: any) {
  const map = new Map();
  list.forEach((item: any) => {
    const key = keyGetter(item);
    const collection = map.get(key);
    if (!collection) {
      map.set(key, [item]);
    } else {
      collection.push(item);
    }
  });
  return map;
}

export const getTaskListFiltersSearchQuery = ({
  filters,
  locationSearch,
  currentPage,
}: {
  filters: ITaskFilters;
  locationSearch?: string;
  currentPage?: number;
}) => {
  const query = new URLSearchParams(locationSearch);
  if (currentPage) {
    query.set('page', String(currentPage));
  }

  Object.values(taskFilterEnum).forEach(taskFilterName => {
    const filterValueInParams = query.get(taskFilterName) || undefined;
    let filterValueInState = filters[taskFilterName];

    if (Array.isArray(filterValueInState)) {
      filterValueInState = filterValueInState?.join(',');
    }

    if (filterValueInState && filterValueInState !== filterValueInParams) {
      query.set(taskFilterName, filterValueInState);
    } else if (!filterValueInState && filterValueInParams) {
      query.delete(taskFilterName);
    }
  });

  return query.toString();
};

export const scrollToElementById = (id: string) => {
  document
    .getElementById(id)
    ?.scrollIntoView({ behavior: 'smooth', block: 'center', inline: 'center' });
};

export const asyncTimeout = (delay: number) => {
  return new Promise(resolve => {
    setTimeout(resolve, delay);
  });
};

export const geTableSortQueryParam = (sort: ITableSort) => {
  if (!sort.field || !sort.order) {
    return;
  }
  const order = sort.order === 'ascend' ? 'asc' : 'desc';

  let fieldName = '';
  if (Array.isArray(sort.field)) {
    fieldName = sort.field.join('.');
  } else {
    fieldName = sort.field;
  }

  return `${fieldName},${order}`;
};

export const getInteger = (text: unknown) => {
  if ((text ?? undefined) === undefined) {
    return undefined;
  }
  const number = Number(text);
  if (Number.isNaN(number)) {
    return undefined;
  }
  return number;
};

export const logoutUser = () => {
  localStorage.removeItem(localStorageEnum.token);
  localStorage.removeItem(localStorageEnum.envId);
  localStorage.removeItem(localStorageEnum.seasonId);

  // reset local state, reload window
  window.location.reload();
};

export const formatUserName = (
  user?: ISimpleUser | IUserListResponseItem | IUser | null,
) => {
  if (!user) {
    return '-';
  }

  return user.name && user.lastName
    ? `${user.name} ${user.lastName}`
    : user.email;
};

export const getTaskStatusText = (status: TaskStatusType) => {
  switch (status) {
    case 'COMPLETED':
      return i18next?.t('Task.StatusType.Completed', 'Completed');
    case 'IN_PROGRESS':
      return i18next?.t('Task.StatusType.InProgress', 'In Progress');
    case 'RECOMMENDED':
      return i18next?.t('Task.StatusType.Recommendation', 'Recommendation');
    case 'PLANNED':
      return i18next?.t('Task.StatusType.Planned', 'Planned');
    case 'DECLINED':
      return i18next?.t('Task.StatusType.Declined', 'Declined');
    case 'ACCEPTED':
      return i18next?.t('Task.StatusType.Accepted', 'Accepted');
    default:
      return '-';
  }
};

export const formatRouteDateFilter = ({
  momentValue,
  isStartOfDay,
}: {
  momentValue: Moment | undefined | null;
  isStartOfDay: boolean;
}) => {
  if (isStartOfDay) {
    return momentValue?.startOf('day').format('YYYY-MM-DD HH:mm:ss');
  }

  return momentValue?.endOf('day').format('YYYY-MM-DD HH:mm:ss');
};

export const randomIntFromInterval = (min: number, max: number) => {
  // min and max included
  return Math.floor(Math.random() * (max - min + 1) + min);
};
